/*
 * "Darstellung" menu in the editor: colour scheme and corners of the
 * project, and its theme (docs/hmi-panel-ausbau.md §3): preset, light or
 * dark, the twelve colours, font, radius and density.
 *
 * A native <details> as the popover. It stays open while the author works
 * in it (every change shows live on the canvas behind it) and closes on a
 * click outside or Escape.
 *
 * Every change goes through `onChange` and with that through the undo
 * history. Dragging a colour picker or the radius slider sends many values
 * in a row; they carry a coalesce key so one gesture is one undo step.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import {
  CORNER_OPTIONS,
  PALETTE_OPTIONS,
  resolveAppearance,
  type AppearanceOption,
  type HmiAppearance,
} from './appearanceModel';
import {
  FONT_OPTIONS,
  THEME_COLOR_KEYS,
  THEME_PRESETS,
  compactTheme,
  isHexColor,
  resolveTheme,
  withThemeColor,
  type HmiTheme,
  type ThemeColors,
  type ThemeDensity,
  type ThemeMode,
} from './themeModel';
import { useSystemDark } from './panel/ThemeScope';
import './HmiAppearanceMenu.css';

interface Props {
  /** The project's appearance; absent means standard. */
  value: HmiAppearance | undefined;
  disabled?: boolean;
  onChange(next: HmiAppearance, coalesceKey?: string): void;
}

const COLOR_LABELS: Readonly<Record<keyof ThemeColors, string>> = {
  background: 'Hintergrund',
  surface: 'Fläche',
  surfaceRaised: 'Fläche erhaben',
  border: 'Rahmen',
  text: 'Text',
  textMuted: 'Text gedämpft',
  value: 'Messwert',
  accent: 'Akzent',
  accentText: 'Text auf Akzent',
  ok: 'In Ordnung',
  warn: 'Warnung',
  alarm: 'Alarm',
};

const MODE_OPTIONS: readonly { value: ThemeMode; label: string }[] = [
  { value: 'auto', label: 'Automatisch' },
  { value: 'light', label: 'Hell' },
  { value: 'dark', label: 'Dunkel' },
];

const DENSITY_OPTIONS: readonly { value: ThemeDensity; label: string; hint: string }[] = [
  { value: 'compact', label: 'Kompakt', hint: 'Kleinere Schrift, mehr auf der Fläche' },
  { value: 'comfortable', label: 'Normal', hint: 'Ausgewogen' },
  { value: 'touch', label: 'Touch', hint: 'Größere Schrift, für Finger und Handschuh' },
];

export function HmiAppearanceMenu({ value, disabled, onChange }: Props) {
  const base = resolveAppearance(value);
  const theme = value?.theme;
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const systemDark = useSystemDark();

  // Close on a click outside or Escape; choices inside keep it open.
  useEffect(() => {
    const onPointer = (ev: PointerEvent) => {
      const el = detailsRef.current;
      if (el?.open && !el.contains(ev.target as Node)) el.open = false;
    };
    const onKey = (ev: KeyboardEvent) => {
      const el = detailsRef.current;
      if (ev.key === 'Escape' && el?.open) {
        el.open = false;
        el.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  if (disabled) return null;

  const emit = (next: Partial<HmiAppearance>, key?: string) => {
    const merged: HmiAppearance = { ...base, ...(theme ? { theme } : {}), ...next };
    if (merged.theme === undefined) delete merged.theme;
    onChange(merged, key);
  };
  const setTheme = (next: HmiTheme | undefined, key?: string) =>
    emit({ theme: next ? compactTheme(next) : undefined }, key);

  const resolved = theme ? resolveTheme(theme, systemDark) : null;
  const preset = resolved ? THEME_PRESETS.find((p) => p.id === resolved.presetId) : null;
  const presetColors = preset ? (resolved!.scheme === 'dark' ? preset.dark : preset.light) : null;

  return (
    <details class="hv-hmi-appearance" ref={detailsRef}
             onToggle={(e) => keepPanelInViewport(e.currentTarget as HTMLDetailsElement)}>
      <summary class="hv-btn hv-hmi-appearance__toggle">
        {resolved && (
          <span class="hv-hmi-appearance__dot" aria-hidden="true"
                style={{ background: resolved.colors.accent, borderColor: resolved.colors.background }} />
        )}
        Darstellung
      </summary>
      <div class="hv-hmi-appearance__panel" role="group" aria-label="Darstellung des HMI">
        <section class="hv-hmi-appearance__section">
          <h4 class="hv-hmi-appearance__heading">Grundlage</h4>
          <Segmented
            title="Farbschema"
            options={PALETTE_OPTIONS}
            value={base.palette}
            onPick={(palette) => emit({ palette })}
          />
          <Segmented
            title="Ecken"
            options={CORNER_OPTIONS}
            value={base.corners}
            onPick={(corners) => emit({ corners })}
          />
        </section>

        <section class="hv-hmi-appearance__section">
          <h4 class="hv-hmi-appearance__heading">
            Theme
            {theme && (
              <button type="button" class="hv-hmi-appearance__link" onClick={() => setTheme(undefined)}>
                Theme entfernen
              </button>
            )}
          </h4>
          {!theme && (
            <p class="hv-hmi-appearance__note">
              Ein Theme legt Farben, Schrift, Ecken und Dichte fest, gleich für Panel, Handy und Web.
              Wähle eine Vorlage als Ausgangspunkt.
            </p>
          )}

          <div class="hv-hmi-appearance__presets" role="radiogroup" aria-label="Vorlage">
            {THEME_PRESETS.map((p) => {
              const active = resolved?.presetId === p.id;
              const scheme = theme?.mode === 'light' || (theme?.mode !== 'dark' && p.defaultMode === 'light')
                ? p.light
                : p.dark;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  class={`hv-hmi-appearance__preset${active ? ' is-active' : ''}`}
                  title={p.hint}
                  onClick={() => setTheme({ ...(theme ?? {}), preset: p.id, colors: theme?.colors })}
                >
                  <span class="hv-hmi-appearance__swatch" style={{ background: scheme.background }}>
                    <span style={{ background: scheme.surfaceRaised, borderRadius: `${Math.min(p.radius, 10)}px` }}>
                      <i style={{ background: scheme.accent }} />
                      <b style={{ color: scheme.value }}>21,5</b>
                    </span>
                  </span>
                  <span class="hv-hmi-appearance__presetName">{p.label}</span>
                </button>
              );
            })}
          </div>

          {theme && resolved && presetColors && (
            <>
              <Segmented
                title="Hell oder dunkel"
                options={MODE_OPTIONS.map((o) => ({ ...o, hint: '' }))}
                value={theme.mode ?? preset!.defaultMode}
                onPick={(mode) => setTheme({ ...theme, mode })}
              />

              <div class="hv-hmi-appearance__field">
                <span class="hv-hmi-appearance__legend">
                  Farben
                  {theme.colors && Object.keys(theme.colors).length > 0 && (
                    <button type="button" class="hv-hmi-appearance__link"
                            onClick={() => setTheme({ ...theme, colors: undefined })}>
                      alle aus der Vorlage
                    </button>
                  )}
                </span>
                <div class="hv-hmi-appearance__colors">
                  {THEME_COLOR_KEYS.map((key) => (
                    <ColorRow
                      key={key}
                      label={COLOR_LABELS[key]}
                      own={theme.colors?.[key]}
                      effective={resolved.colors[key]}
                      fallback={presetColors[key]}
                      onChange={(next, live) =>
                        setTheme(withThemeColor(theme, key, next), live ? `theme:color:${key}` : undefined)}
                    />
                  ))}
                </div>
              </div>

              <label class="hv-hmi-appearance__field">
                <span class="hv-hmi-appearance__legend">Schrift</span>
                <select
                  class="hv-hmi-appearance__select"
                  value={resolved.font.id}
                  style={{ fontFamily: resolved.font.stack }}
                  onChange={(e) => setTheme({ ...theme, font: (e.currentTarget as HTMLSelectElement).value })}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.id} value={f.id} style={{ fontFamily: f.stack }}>{f.label}</option>
                  ))}
                </select>
              </label>

              <label class="hv-hmi-appearance__field">
                <span class="hv-hmi-appearance__legend">
                  Eckenradius <output>{resolved.radius} px</output>
                </span>
                <span class="hv-hmi-appearance__range">
                  <input
                    type="range"
                    min={0}
                    max={48}
                    step={1}
                    value={resolved.radius}
                    onInput={(e) => setTheme(
                      { ...theme, radius: Number((e.currentTarget as HTMLInputElement).value) },
                      'theme:radius',
                    )}
                  />
                  <span class="hv-hmi-appearance__radiusDemo" style={{ borderRadius: `${resolved.radius}px` }} />
                </span>
              </label>

              <Segmented
                title="Dichte"
                options={DENSITY_OPTIONS}
                value={resolved.density}
                onPick={(density) => setTheme({ ...theme, density })}
              />
            </>
          )}
        </section>

        <p class="hv-hmi-appearance__note">
          Gilt für alle, die dieses HMI ansehen. Mit "Automatisch" folgt es hell oder dunkel des Geräts.
        </p>
      </div>
    </details>
  );
}

/**
 * Shift an opened popover (the element after <summary>) so it stays inside
 * the window. The menus sit in a toolbar that wraps; depending on the
 * window width the button is anywhere from the left edge to the right one.
 */
export function keepPanelInViewport(details: HTMLDetailsElement): void {
  const panel = details.querySelector<HTMLElement>(':scope > div');
  if (!panel) return;
  panel.style.translate = '';
  if (!details.open) return;
  const r = panel.getBoundingClientRect();
  const margin = 8;
  let shift = 0;
  if (r.right > window.innerWidth - margin) shift = window.innerWidth - margin - r.right;
  if (r.left + shift < margin) shift = margin - r.left;
  if (shift !== 0) panel.style.translate = `${Math.round(shift)}px 0`;
}

function Segmented<T extends string>({
  title,
  options,
  value,
  onPick,
}: {
  title: string;
  options: readonly AppearanceOption<T>[];
  value: T;
  onPick(next: T): void;
}) {
  return (
    <fieldset class="hv-hmi-appearance__field hv-hmi-appearance__group">
      <legend class="hv-hmi-appearance__legend">{title}</legend>
      <div class="hv-hmi-appearance__segmented">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            class={`hv-hmi-appearance__seg${o.value === value ? ' is-active' : ''}`}
            aria-pressed={o.value === value}
            title={o.hint || undefined}
            onClick={() => onPick(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * One theme colour: picker, hex field, back-to-preset. Empty means the
 * preset's colour; the field then shows it as placeholder, so the author
 * sees what applies without it being written into the project.
 */
function ColorRow({
  label,
  own,
  effective,
  fallback,
  onChange,
}: {
  label: string;
  own: string | undefined;
  effective: string;
  fallback: string;
  /** `live` is true while the picker is being dragged. */
  onChange(next: string | undefined, live: boolean): void;
}) {
  const [draft, setDraft] = useState(own ?? '');
  useEffect(() => setDraft(own ?? ''), [own]);
  const invalid = draft !== '' && !isHexColor(draft);

  const commitDraft = () => {
    if (draft === '') onChange(undefined, false);
    else if (isHexColor(draft)) onChange(draft, false);
  };

  return (
    <div class={`hv-hmi-appearance__color${own ? ' is-own' : ''}`}>
      <input
        type="color"
        class="hv-hmi-appearance__picker"
        value={effective}
        aria-label={`${label}: Farbwähler`}
        onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value, true)}
      />
      <span class="hv-hmi-appearance__colorLabel" title={label}>{label}</span>
      <input
        type="text"
        class={`hv-hmi-appearance__hex${invalid ? ' is-invalid' : ''}`}
        value={draft}
        placeholder={fallback}
        maxLength={7}
        spellcheck={false}
        aria-label={`${label}: Farbwert`}
        aria-invalid={invalid}
        onInput={(e) => {
          const raw = (e.currentTarget as HTMLInputElement).value.trim();
          setDraft(raw);
          if (raw === '' || isHexColor(raw)) onChange(raw === '' ? undefined : raw, false);
        }}
        onBlur={commitDraft}
      />
      <button
        type="button"
        class="hv-hmi-appearance__reset"
        disabled={!own}
        title="Farbe aus der Vorlage nehmen"
        aria-label={`${label}: Farbe aus der Vorlage nehmen`}
        onClick={() => onChange(undefined, false)}
      >
        ×
      </button>
    </div>
  );
}
